import React, { useEffect, useRef, useCallback } from 'react';
import './BadWordsModal.css';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverMessageStyle,
  popoverButtonContainerStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared Bad Words Modal Component
 * Issue #161: Inform user what kinds of bad words block them to upload content
 *
 * Displays a modal showing which inappropriate words were detected in the content.
 * Uses proper grammar based on content type (post, object, comment, review).
 * Full keyboard accessibility support (WCAG 2.1, EN 301 549).
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close callback
 * @param {string[]} badWords - Array of detected bad words
 * @param {string} contentType - 'post' | 'object' | 'comment' | 'review'
 */
const BadWordsModal = ({
  isOpen,
  onClose,
  badWords = [],
  contentType = 'post'
}) => {
  const { t } = useLanguage();
  const modalRef = useRef(null);
  const buttonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Get the appropriate message based on content type
  const getMessage = () => {
    switch (contentType) {
      case 'object':
        return t('badWords.objectMessage');
      case 'comment':
        return t('badWords.commentMessage');
      case 'review':
        return t('badWords.reviewMessage');
      case 'post':
      default:
        return t('badWords.postMessage');
    }
  };

  // Store previously focused element and focus button when modal opens
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 10);
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={popoverContainerStyle}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="bad-words-modal-title"
        aria-describedby="bad-words-modal-description"
        tabIndex={-1}
      >
        <h2 id="bad-words-modal-title" style={popoverHeaderStyle}>
          {t('badWords.title')}
        </h2>

        <div id="bad-words-modal-description">
          <p style={popoverMessageStyle}>
            {getMessage()}
          </p>

          {badWords.length > 0 && (
            <ul
              className="bad-words-list"
              aria-label={t('badWords.title')}
            >
              {badWords.map((word, index) => (
                <li key={index} className="bad-word-item">
                  {word}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={popoverButtonContainerStyle}>
          <button
            ref={buttonRef}
            type="button"
            className="bad-words-modal-button"
            onClick={onClose}
            style={getSubmitButtonStyle(false)}
            aria-label={t('badWords.understood')}
          >
            {t('badWords.understood')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadWordsModal;
